import { Link } from "react-router-dom";
import { conversationLinkFor } from "@/lib/activityLabels";
import { cn } from "@/lib/utils";
import { useStore } from "@/store";

export interface ConversationLinkProps {
  // Any document carrying the backend's provenance stamp. `domain_id` is
  // optional because path-scoped documents (WordPress versions) don't store one
  // — see the fallback below.
  entity: {
    domain_id?: string | null;
    source_thread_id?: string | null;
    source_message_id?: string | null;
  } | null;
  label?: string;
  className?: string;
}

/**
 * Link back to the conversation turn that produced a document, or nothing when
 * it wasn't produced by one. Hand-made and pre-stamp documents are the common
 * case at every call site, so rendering null is normal, not an error path.
 */
export function ConversationLink({
  entity,
  label = "From a conversation",
  className,
}: ConversationLinkProps) {
  const currentDomainId = useStore((state) => state.currentDomain?.id);

  // Entity-first: a document that knows its own domain must never be linked
  // with the currently selected one. The fallback exists for `WordPressVersion`,
  // which is path-scoped under a page and stores no `domain_id` (nor does `Page`).
  const href = entity
    ? conversationLinkFor({
        ...entity,
        domain_id: entity.domain_id ?? currentDomainId,
      })
    : null;

  if (!href) {
    return null;
  }

  return (
    <Link
      to={href}
      className={cn(
        "text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground",
        className,
      )}
    >
      {label}
    </Link>
  );
}
